import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import { useChatStore } from '@/stores/useChatStore';
import { useSessionStore } from '@/stores/useSessionStore';
import type { ModelLabel } from '@/types/session';
import { useAuthStore } from '@/stores/useAuthStore';
import { hasPermission } from '@/utils/permissions';
import { useDraftGuard } from '@/hooks/useDraftGuard';

const INPUT_HEIGHT = 72;

interface MessageInputProps {
  target?: ModelLabel | 'both';
  compact?: boolean;
  disabled?: boolean;
  startStream: (input: string, target: ModelLabel | 'both') => void;
}

/**
 * 底部输入区（72px 高）。
 * 多行 Textarea，Enter 发送，Shift+Enter 换行，左侧配置快捷入口。
 */
const MessageInput: React.FC<MessageInputProps> = ({ target = 'both', compact = false, disabled = false, startStream }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  useDraftGuard(Boolean(text.trim()));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentSession = useSessionStore((s) => s.currentSession);
  const permissions = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const actions = useAuthStore((s) => s.actions);
  const canCompose = hasPermission(permissions, 'practice', 'write') && hasPermission(permissions, 'conversations', 'write');
  const canWrite = canCompose &&
    !disabled &&
    (target !== 'both' || actions['practice.compare']) &&
    (currentSession?.user_id === user?.user_id ||
      (actions['conversations.read_all'] && actions['conversations.edit_all']));
  const isStreamingLeft = useChatStore((s) => s.isStreamingLeft);
  const isStreamingRight = useChatStore((s) => s.isStreamingRight);
  const sendMessageToStore = useChatStore((s) => s.sendMessage);

  const isStreaming =
    target === 'left' ? isStreamingLeft : target === 'right' ? isStreamingRight : isStreamingLeft || isStreamingRight;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming || !currentSession || !canWrite) return;

    sendMessageToStore(trimmed, target);
    setText('');

    // 启动 SSE 流
    startStream(trimmed, target);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  return (
    <Box
      sx={{
        height: 'auto',
        minHeight: compact ? 64 : INPUT_HEIGHT,
        flexShrink: 0,
        px: compact ? 1.5 : 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {!compact && hasPermission(permissions, 'config') && (
        <Tooltip title="打开配置">
          <IconButton
            size="small"
            onClick={() => navigate('/config')}
            sx={{ color: 'text.secondary' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* 输入框 */}
      <TextField
        inputRef={textareaRef}
        fullWidth
        multiline
        maxRows={3}
        placeholder={
          isStreaming
            ? '正在生成...'
            : !canCompose ? '当前账号仅可查看'
            : currentSession && !canWrite ? '此会话不可编辑'
            : currentSession
            ? target === 'both'
              ? '输入你的问题...'
              : compact ? '继续追问这一侧...' : '输入你的问题...'
            : '请先创建会话'
        }
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={!canWrite || isStreaming || !currentSession}
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            borderRadius: 2,
          },
        }}
      />

      {/* 发送按钮 */}
      <IconButton
        aria-label={target === 'both' ? '同时发送' : `发送给${target === 'left' ? '模型 A' : '模型 B'}`}
        color="primary"
        onClick={handleSend}
        disabled={!canWrite || isStreaming || !text.trim() || !currentSession}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
        }}
      >
        {isStreaming ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <SendIcon />
        )}
      </IconButton>
    </Box>
  );
};

export default MessageInput;
